import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Activity,
  LayoutDashboard,
  GitBranch,
  BrainCircuit,
  Settings,
  LogOut,
  Sun,
  Moon,
  Search,
  ChevronDown,
  Zap,
  LineChart,
  Gauge,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

const NAV_GROUPS = [
  {
    label: "Data Visualisation",
    items: [
      { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
      { to: "/events", label: "Event Logs", icon: Activity, testid: "nav-events" },
    ],
  },
  {
    label: "Workflows Management",
    items: [
      { to: "/workflows", label: "Capture Workflows", icon: GitBranch, testid: "nav-workflows" },
      { to: "/detection", label: "Detection Engine", icon: BrainCircuit, testid: "nav-detection" },
      { to: "/measure", label: "Measure", icon: Gauge, testid: "nav-measure" },
      { to: "/config", label: "Configuration", icon: Settings, testid: "nav-config" },
    ],
  },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Sidebar */}
      <aside
        data-testid="app-sidebar"
        className={cn(
          "sidebar-panel flex flex-col transition-[width] duration-300",
          collapsed ? "w-16" : "w-64"
        )}
      >
        {/* Brand */}
        <div className="h-16 flex items-center gap-2.5 px-4 border-b sidebar-divider">
          <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center shrink-0">
            <Zap className="w-4 h-4 text-primary-foreground" strokeWidth={2.2} />
          </div>
          {!collapsed && (
            <div className="flex flex-col leading-tight">
              <span className="font-display text-base font-semibold tracking-tight">OptiKurve</span>
              <span className="label-caps text-[0.6rem]">Event Gateway</span>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 px-2 space-y-4 overflow-auto">
          {NAV_GROUPS.map((group) => (
            <div key={group.label} className="space-y-1">
              {!collapsed && <div className="label-caps px-3 pb-1.5">{group.label}</div>}
              {collapsed && <div className="border-t sidebar-divider mx-2 my-2" />}
              {group.items.map(({ to, label, icon: Icon, testid }) => (
                <NavLink
                  key={to}
                  to={to}
                  data-testid={testid}
                  className={({ isActive }) =>
                    cn(
                      "sidebar-nav-link flex items-center gap-3 px-3 py-2 rounded-md text-sm",
                      isActive && "active"
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon className="w-4 h-4 shrink-0" strokeWidth={1.5} />
                      {!collapsed && <span data-active={isActive}>{label}</span>}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {/* Collapse button */}
        <button
          data-testid="sidebar-collapse-btn"
          onClick={() => setCollapsed((c) => !c)}
          className="mx-2 mb-3 mt-1 p-2 text-xs text-[hsl(var(--sidebar-muted))] hover:text-[hsl(var(--sidebar-fg))] border-t sidebar-divider pt-3 flex items-center gap-2"
        >
          <ChevronDown
            className={cn("w-3.5 h-3.5 transition-transform", collapsed ? "-rotate-90" : "rotate-90")}
          />
          {!collapsed && <span>Collapse</span>}
        </button>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-16 border-b border-border flex items-center justify-between px-6 gap-4 bg-background/60 backdrop-blur-xl sticky top-0 z-40">
          <div className="flex items-center gap-3 flex-1 max-w-md">
            <Search className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
            <input
              data-testid="global-search"
              placeholder="Search events, workflows, customers…"
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
            <kbd className="hidden md:inline-flex text-[0.65rem] font-mono px-1.5 py-0.5 border border-border rounded text-muted-foreground">⌘K</kbd>
          </div>

          <div className="flex items-center gap-2">
            <div className="hidden md:flex items-center gap-1.5 text-xs text-muted-foreground border border-border rounded-full px-2.5 py-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 pulse-dot" />
              <span>Ingestion live · 3,842 evt/min</span>
            </div>

            <Button
              variant="ghost"
              size="icon"
              onClick={toggle}
              data-testid="theme-toggle"
              className="h-9 w-9"
            >
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-trigger"
                  className="flex items-center gap-2 px-2 py-1.5 hover:bg-secondary rounded-md text-sm"
                >
                  <div className="w-8 h-8 rounded-md bg-primary/10 border border-primary/30 text-primary font-display font-semibold text-sm flex items-center justify-center">
                    {user?.avatar}
                  </div>
                  <div className="hidden md:flex flex-col leading-tight text-left">
                    <span className="text-xs font-medium">{user?.email}</span>
                    <span className="text-[0.65rem] text-muted-foreground">{user?.role}</span>
                  </div>
                  <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/config")} data-testid="user-menu-config">
                  <Settings className="w-3.5 h-3.5 mr-2" /> Configuration
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} data-testid="user-menu-logout">
                  <LogOut className="w-3.5 h-3.5 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
